import React, { useMemo } from 'react';
import { Button, Flex, Text } from '@tishman/components';

import { formatMoney } from '../../../../utils';
import { actions, useAppDispatch } from '../../../../store';
import { getRelevantStatGroup } from '../../../../utils/compare-statgroups';
import { newRelic } from '../../../../utils/new-relic';
import {
  parseTimeslotHours,
  parseTimeslotTime,
} from '../../../../utils/parse-timeslot-hours';
import { formatTimeRangeDisplay, safeDataLayerPush } from '../../utils';
import { parseAndFormatDate } from '../../../../services/date';
import { Flow, type StatGroup } from '../../../../services/viva/constants';
import { putInArray } from '../../../../services/viva/utils';

import type { ShopCartSelection } from '../../../../store/types';
import type { AddOnItem } from '../../AddOns';
import type { CartContentTranslationsForFlow } from '../../../../data/translations/cartContent';

type Props = {
  selection: ShopCartSelection | AddOnItem;
  translations: CartContentTranslationsForFlow;
  isAddOn?: boolean;
  dateTime?: number;
  flow: Flow;
};

export const Ticket: React.FC<Props> = ({
  dateTime,
  selection,
  translations,
  flow,
  isAddOn = false,
}) => {
  const dispatch = useAppDispatch();
  const statGroups = selection.statGroup;
  const statGroup = getRelevantStatGroup(statGroups as StatGroup);

  const hours = useMemo(() => {
    if ('timeSlot' in selection && selection.timeSlot) {
      return parseTimeslotHours(selection.timeSlot);
    }

    return [];
  }, [selection]);

  const selectionDates =
    'timeSlot' in selection && selection.timeSlot
      ? parseTimeslotTime(selection.timeSlot)
      : [];

  const qty =
    ('count' in selection ? selection.count : selection.quantity) ?? 1;

  const remove = () => {
    if (selection.statGroup) {
      if (isAddOn && dateTime) {
        dispatch(
          actions.checkout.removeAddOnItemFromCheckoutByFlowAndDateTime({
            flow,
            dateTime,
            statGroup: statGroup as StatGroup,
          }),
        );
      } else if (
        'dateTime' in selection &&
        selection.statGroup &&
        selection.dateTime
      ) {
        dispatch(
          actions.checkout.removeItemFromCheckoutCartByTypeAndDateTime({
            flow,
            dateTime: selection.dateTime,
            type: selection.type,
          }),
        );
      }
    }

    const removedItems = [
      {
        item_id: 'productAK' in selection ? selection.productAK : selection.ak,
        item_name:
          'label' in selection
            ? selection.label
            : selection.name ?? selection.description,
        item_category: flow,
        price: (('price' in selection ? selection.price : 0) / 100).toFixed(2),
        quantity: 'count' in selection ? selection.count : selection.quantity,
        discount:
          'discount' in selection && selection.discount
            ? selection.discount
            : '0.00',
        item_flow: flow,
      },
    ];

    newRelic.logCartAction(`Ticket - Remove clicked in Modal`, 'Remove', {
      removedItems,
    });

    safeDataLayerPush({
      event: 'remove_from_cart',
      remove_items: removedItems,
    });
  };

  return (
    <Flex
      sx={{
        flexDirection: 'column',
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          backgroundColor: '#fafafa',
          px: ['12px'],
          py: ['8px'],
        }}
      >
        <Flex
          sx={{
            justifyContent: 'space-between',
          }}
        >
          <Text>
            {qty}x{' '}
            {
              translations.ticketTypes[
                getRelevantStatGroup(statGroup as StatGroup)
              ]?.title
            }
          </Text>
          <Text>{formatMoney(selection.price * qty, undefined, true)}</Text>
        </Flex>
        {'timeSlot' in selection &&
          !!selection.timeSlot &&
          selectionDates.map((date, index) => (
            <Flex
              key={date}
              sx={{
                justifyContent: 'space-between',
              }}
            >
              {date && index === 0 && (
                <Text
                  data-date={parseAndFormatDate(date, 'MMMM d, yyyy')}
                  data-testid="ticket-date"
                >
                  {parseAndFormatDate(date, 'MMMM d, yyyy')}
                </Text>
              )}
              {index > 0 && <div />}
              <Text data-testid="ticket-time-slot">
                {formatTimeRangeDisplay(
                  putInArray(selection.timeSlot),
                  index,
                  flow === Flow.ON_THE_ROCKS,
                )}
              </Text>
            </Flex>
          ))}
        <Button
          onClick={remove}
          sx={{
            alignSelf: 'flex-start',
            backgroundColor: 'transparent',
            letterSpacing: 'unset',
            mt: '4px',
            p: 0,
            textTransform: 'capitalize',
          }}
          variant="underline"
        >
          <Text>remove</Text>
        </Button>
      </Flex>
    </Flex>
  );
};
